import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AxiosResponse } from "axios";
import axios from "../axios";
import axiosWithoutInterceptor from "../axiosWithoutInterceptor";
import { buildApiV3Url, buildApiV3UrlWithQuery } from "../shared/Utils";
import { RootState } from "./types";

// Types
export interface EcmsRecord {
    id: number;
    customerName: string;
    customerCode: string;
    avaCertId: string;
    exemptCertId: string;
    country: string;
    region: string;
    exemptReasonId: string;
    businessTypeId: string;
    exemptCertStatusId: string;
}

export interface PaginateState {
    pageNumber: number;
    startIndex: number;
    endIndex: number;
    rowsPerPage: number;
    totalRecords: number;
    indeterminate: boolean;
}

export interface PageState {
    data: EcmsRecord[];
    paginate: PaginateState;
}

export interface EcmsFiltersState {
    companyId: string;
    customerName: string;
    customerCode: string;
    avaCertId: string;
    exemptCertId: string;
    country: string[];
    region: string[];
}

export interface EcmsState {
    isLoading: boolean;
    page: PageState;
    sortColumn: [string, boolean];
    filtersState: EcmsFiltersState;
    hasFilterChips: boolean;
    error: string | null;
}

interface RevokeEcmsCertParams {
    ecmsCertId: number | string;
    auditMessage: string;
}

interface FetchEcmsParams {
    filterString?: string;
    top?: number;
    skip?: number;
    paginateDetails?: {
        pageNumber: number;
        startIndex: number;
        endIndex: number;
        rowsPerPage: number;
    };
}

interface FetchEcmsResponse {
    data: EcmsRecord[];
    paginate: PaginateState;
}

interface EcmsApiResponse {
    value: EcmsRecord[] | null;
    count: number;
}

export const initialFiltersState: EcmsFiltersState = {
    companyId: "",
    customerName: "",
    customerCode: "",
    avaCertId: "",
    exemptCertId: "",
    country: [],
    region: [],
};

export const initialState: EcmsState = {
    isLoading: false,
    page: {
        data: [],
        paginate: {
            pageNumber: 0,
            startIndex: -1,
            endIndex: 0,
            rowsPerPage: 10,
            totalRecords: 0,
            indeterminate: false,
        },
    },
    sortColumn: ["customerName", false],
    filtersState: initialFiltersState,
    hasFilterChips: false,
    error: null,
};

/**
 * Build filter string from ECMS filters state
 * @param filters - ECMS filters state
 * @returns OData filter string
 */
/**
 * Escape single quotes in OData filter values
 * OData requires single quotes to be escaped as two single quotes
 */
const escapeODataString = (value: string): string => {
    return value.replace(/'/g, "''");
};

/**
 * OData $filter for ECMS list. Company scope is only via URL (`companies/{companyId}/ecms`), never here.
 */
const buildEcmsFilterString = (filters: EcmsFiltersState): string => {
    const filterParts: string[] = [];

    /**
     * AvaTax API has LIMITED OData support - only basic operators:
     * - eq, ne, gt, lt, ge, le (comparison)
     * - AND, OR (logical)
     *
     * NO support for: contains, tolower, startswith, etc.
     *
     * For text fields (customerName, customerCode, avaCertId), we cannot filter server-side.
     * Only exact-match filters are supported.
     */

    // Text search fields - use exact match only
    // Note: This is a limitation of AvaTax API - no partial text search support
    if (filters.customerName) {
        const escaped = escapeODataString(filters.customerName);
        filterParts.push(`customerName eq '${escaped}'`);
    }
    if (filters.customerCode) {
        const escaped = escapeODataString(filters.customerCode);
        filterParts.push(`customerCode eq '${escaped}'`);
    }
    if (filters.avaCertId) {
        const escaped = escapeODataString(filters.avaCertId);
        filterParts.push(`avaCertId eq '${escaped}'`);
    }
    if (filters.exemptCertId) {
        // exemptCertId is numeric
        filterParts.push(`exemptCertId eq ${filters.exemptCertId}`);
    }

    // Array filters - use 'or' to combine multiple values
    if (filters.country && filters.country.length > 0) {
        const countryFilters = filters.country
            .map(c => `country eq '${escapeODataString(c)}'`)
            .join(" or ");
        filterParts.push(`(${countryFilters})`);
    }

    // Note: Region filter is disabled in the UI due to backend API bug
    // The AvaTax ECMS API returns 500 error: "The request message was already sent. Cannot send the same request message multiple times."
    // This is caused by HttpRequestMessage reuse in the webapi RestRequest implementation
    // Keep this code for when the backend issue is resolved
    if (filters.region && filters.region.length > 0) {
        const regionFilters = filters.region
            .map(r => `region eq '${escapeODataString(r)}'`)
            .join(" or ");
        filterParts.push(`(${regionFilters})`);
    }

    return filterParts.length > 0 ? filterParts.join(" and ") : "";
};

/**
 * Async thunk for fetching ECMS records with pagination, sorting, and filtering
 * @param params - Fetch parameters including filter, pagination details
 * @returns Promise with ECMS data and pagination info
 */
export const fetchEcms = createAsyncThunk<
    FetchEcmsResponse,
    FetchEcmsParams | undefined,
    { rejectValue: string }
    // eslint-disable-next-line default-param-last
>("ecms/fetchEcms", async (params = {}, thunkAPI) => {
    try {
        const { filterString, top, skip, paginateDetails } = params;
        const state = thunkAPI.getState() as RootState;
        const { session, ecms } = state;
        const finalPageSize = top || session?.rowsPerPage;
        const sortColumn = ecms?.sortColumn || initialState.sortColumn;

        const sort = `${sortColumn[0]} ${sortColumn[1] ? "ASC" : "DESC"}`;

        const companyId = ecms.filtersState.companyId?.trim();
        const useCompanyPath = Boolean(companyId);

        // Build filter string from filters state if not provided
        const finalFilterString =
            filterString !== undefined ? filterString : buildEcmsFilterString(ecms.filtersState);

        const ecmsRelativePath = useCompanyPath ? `companies/${companyId}/ecms` : "ecms";

        const apiURI = buildApiV3UrlWithQuery(
            ecmsRelativePath,
            finalFilterString || null,
            null,
            finalPageSize,
            skip,
            sort
        );

        const response: AxiosResponse<EcmsApiResponse> = await axios.get(apiURI, {
            withCredentials: true,
        });

        if (response?.status === 200 && response?.data?.value !== null) {
            const paginateData: PaginateState = paginateDetails
                ? {
                      pageNumber: paginateDetails.pageNumber,
                      startIndex: paginateDetails.startIndex,
                      endIndex: paginateDetails.endIndex,
                      rowsPerPage: paginateDetails.rowsPerPage,
                      totalRecords: response.data.count,
                      indeterminate: false,
                  }
                : {
                      pageNumber: 1,
                      startIndex: 0,
                      endIndex: finalPageSize - 1,
                      rowsPerPage: finalPageSize,
                      totalRecords: response.data.count,
                      indeterminate: false,
                  };
            return { data: response.data.value, paginate: paginateData };
        } else {
            return thunkAPI.rejectWithValue("No data found");
        }
    } catch (error: unknown) {
        if (error instanceof Error) {
            return thunkAPI.rejectWithValue(error.message);
        }
        return thunkAPI.rejectWithValue("An unknown error occurred");
    }
});

/**
 * Async thunk for revoking an ECMS certificate
 * @param params - ecmsCertId and auditMessage for the revoke request
 * @returns Promise that resolves on success or rejects with error message
 */
export const revokeEcmsCert = createAsyncThunk<void, RevokeEcmsCertParams, { rejectValue: string }>(
    "ecms/revokeEcmsCert",
    async ({ ecmsCertId, auditMessage }, thunkAPI) => {
        try {
            const url = buildApiV3Url(`${ecmsCertId}/revoke`);
            await axiosWithoutInterceptor.patch(
                url,
                { AuditMessage: auditMessage },
                { withCredentials: true }
            );
            return;
        } catch (error: unknown) {
            if (error instanceof Error) {
                return thunkAPI.rejectWithValue(error.message);
            }
            return thunkAPI.rejectWithValue("An unknown error occurred");
        }
    }
);

const ecmsSlice = createSlice({
    name: "ecms",
    initialState,
    reducers: {
        setPage: (state, action: PayloadAction<PageState>) => {
            state.page.data = action.payload.data;
            state.page.paginate = action.payload.paginate;
        },
        setPageToNull: state => {
            state.page = initialState.page;
        },
        setSortColumn: (state, action: PayloadAction<[string, boolean]>) => {
            state.sortColumn = action.payload;
        },
        setEcmsFiltersState: (state, action: PayloadAction<Partial<EcmsFiltersState>>) => {
            state.filtersState = { ...state.filtersState, ...action.payload };
        },
        clearEcmsFiltersState: state => {
            state.filtersState = initialFiltersState;
        },
        setHasFilterChips: (state, action: PayloadAction<boolean>) => {
            state.hasFilterChips = action.payload;
        },
    },
    extraReducers: builder => {
        builder.addCase(fetchEcms.pending, state => {
            state.isLoading = true;
            state.error = null;
        });
        builder.addCase(fetchEcms.fulfilled, (state, action) => {
            state.isLoading = false;
            state.error = null;
            if (action.payload?.data) {
                state.page.data = action.payload.data;
                state.page.paginate = action.payload.paginate;
            } else {
                state.page = initialState.page;
            }
        });
        builder.addCase(fetchEcms.rejected, (state, action) => {
            state.isLoading = false;
            state.error = action.payload || "Failed to fetch ECMS data";
            // Clear the grid data on error
            state.page = initialState.page;
        });
        builder.addCase(revokeEcmsCert.fulfilled, state => {
            state.error = null;
        });
        builder.addCase(revokeEcmsCert.rejected, (state, action) => {
            state.error = action.payload || "Failed to revoke ECMS certificate";
        });
    },
});

export const {
    setPage,
    setSortColumn,
    setPageToNull,
    setEcmsFiltersState,
    clearEcmsFiltersState,
    setHasFilterChips,
} = ecmsSlice.actions;

// Selectors
export const selectEcms = (state: RootState): PageState => state.ecms.page;

export const selectEcmsIsLoading = (state: RootState): boolean => state.ecms.isLoading;

export const selectSortColumn = (state: RootState): [string, boolean] => state.ecms.sortColumn;

export const selectEcmsFiltersState = (state: RootState): EcmsFiltersState =>
    state.ecms.filtersState;

export const selectHasFilterChips = (state: RootState): boolean => state.ecms.hasFilterChips;

export const selectEcmsError = (state: RootState): string | null => state.ecms.error;

export default ecmsSlice.reducer;
